import { OptionsProp, MoreFilterItem, IDoubleSelect } from '../filter'
import { transNumberToUnit } from '../custom-render'
import { useEffect, useState, useMemo, useCallback } from 'react'
import useLocale from '@/utils/useLocale'
import { DatePicker, Button, InputNumber, Divider, Radio, Tooltip, Dropdown } from '@arco-design/web-react'
import { IconDown, IconUp } from '@arco-design/web-react/icon'
import dayjs from 'dayjs'
import { useSetState, useUpdateEffect } from 'ahooks'
import FilterItem, { FilterItemType } from '@/components/FilterItem'
import styles from '../style/filter.module.less'
import styles1 from '@/components/FilterItem/index.module.less'
import { cn } from '@/utils/lib'

const RadioGroup = Radio.Group

export function resRealValue(options: OptionsProp | undefined, value: any, unit = '', suffix = '') {
  if (!options) return ''
  let resultText = ''
  const knowObj = options?.find((item) => item.id === value)
  if (knowObj) {
    resultText = knowObj.name
  } else {
    resultText = transNumberToUnit(value, unit, suffix)
  }
  return resultText
}

export function resRealAnyValue(options: OptionsProp | undefined, value: any) {
  if (!options) return ''
  let resultText = ''
  if (typeof value === 'string') {
    const knowObj = options?.find((item) => item.id === value)
    if (knowObj) {
      resultText = knowObj.name
    }
  } else if (Array.isArray(value)) {
    value.forEach((val) => {
      const knowObj = options?.find((item) => item.id === val)
      if (knowObj) {
        resultText += `${knowObj.name}&`
      }
    })
    if (resultText.indexOf('&') > -1) resultText = resultText.slice(0, -1)
  }

  return resultText
}

interface State {
  [key: string]: any
}

export interface NewDateRangePickerProps {
  params: { [key: string]: any }
  onChange: (params: { [key: string]: any }) => void
  tooltipText?: string
}

const NewDateRangePicker = ({ params, onChange, tooltipText }: NewDateRangePickerProps) => {
  const t = useLocale()
  return (
    <div className={''}>
      {tooltipText ? (
        <Tooltip content={tooltipText}>
          <span>
            <DatePicker.RangePicker
              mode="date"
              className={styles.datePicker}
              triggerProps={{
                // popupVisible: true,
                className: styles.datePickerPopup,
                position: 'bl'
              }}
              shortcuts={[
                {
                  text: t['product.filter.yesterday'],
                  value: () => [dayjs().startOf('day'), dayjs().subtract(1, 'days').startOf('day')]
                },
                {
                  text: t['product.filter.last_week'],
                  value: () => [dayjs().startOf('day'), dayjs().subtract(7, 'days').startOf('day')]
                },
                {
                  text: t['product.filter.last_month'],
                  value: () => [dayjs().startOf('day'), dayjs().subtract(30, 'days').startOf('day')]
                },
                {
                  text: t['product.filter.last90'],
                  value: () => [dayjs().startOf('day'), dayjs().subtract(90, 'days').startOf('day')]
                }
              ]}
              disabledDate={(current) => current.isAfter(dayjs())}
              defaultPickerValue={[dayjs().month(dayjs().month() - 1), dayjs()]}
              value={
                params['start_time'] && params['end_time']
                  ? [dayjs.unix(params['start_time']), dayjs.unix(params['end_time'])]
                  : undefined
              }
              onChange={(_, date) => {
                onChange({
                  start_time: date ? String(date?.[0].unix()) : '',
                  end_time: date ? String(date?.[1].unix()) : ''
                })
              }}
            />
          </span>
        </Tooltip>
      ) : (
        <DatePicker.RangePicker
          mode="date"
          className={styles.datePicker}
          triggerProps={{
            // popupVisible: true,
            className: styles.datePickerPopup,
            position: 'left'
          }}
          shortcuts={[
            {
              text: t['product.filter.yesterday'],
              value: () => [dayjs().startOf('day'), dayjs().subtract(1, 'days').startOf('day')]
            },
            {
              text: t['product.filter.last_week'],
              value: () => [dayjs().startOf('day'), dayjs().subtract(7, 'days').startOf('day')]
            },
            {
              text: t['product.filter.last_month'],
              value: () => [dayjs().startOf('day'), dayjs().subtract(30, 'days').startOf('day')]
            },
            {
              text: t['product.filter.last90'],
              value: () => [dayjs().startOf('day'), dayjs().subtract(90, 'days').startOf('day')]
            }
          ]}
          disabledDate={(current) => current.isAfter(dayjs())}
          defaultPickerValue={[dayjs().month(dayjs().month() - 1), dayjs()]}
          value={
            params['start_time'] && params['end_time']
              ? [dayjs.unix(params['start_time']), dayjs.unix(params['end_time'])]
              : undefined
          }
          onChange={(_, date) => {
            onChange({
              start_time: date ? String(date?.[0].unix()) : '',
              end_time: date ? String(date?.[1].unix()) : ''
            })
          }}
        />
      )}
    </div>
  )
}

export const NewMultipleCheckbox = ({ filter, params, onChange }: IDoubleSelect) => {
  const optionMap = filter.optionMap
  const keys = useMemo(() => Object.keys(optionMap ? optionMap : {}) || [], [optionMap])
  const [state, setState] = useSetState<State>({})

  useUpdateEffect(() => {
    Object.keys(params).forEach((key) => {
      if (keys.includes(key)) {
        // url有默认值
        setState({ [key]: params[key] })
      }
    })
  }, [params])

  const DropList = () => {
    return (
      <div className={styles['multiple-checkbox-wrap']}>
        {keys?.map((key) => {
          return (
            <div className={styles['multiple-checkbox-item']} key={key}>
              <label className={styles['multiple-checkbox-item-label']}>{optionMap?.[key].name}：</label>
              <div className={styles['multiple-checkbox-item-result']}>
                <RadioGroup
                  value={state[key]}
                  defaultValue={params?.[key] || ''}
                  onChange={(value: any) => {
                    setState({
                      [key]: value
                    })
                    onChange?.({
                      [key]: value
                    })
                  }}
                >
                  {optionMap?.[key]?.data?.map((item) => {
                    return (
                      <Radio value={item.id} key={item.id}>
                        {item.name}
                      </Radio>
                    )
                  })}
                </RadioGroup>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div>
      <Dropdown droplist={<DropList />} position="bl" trigger={'click'}>
        <div className={styles['trigger-button']}>
          {filter.label} <IconDown style={{ paddingLeft: 10 }} />
        </div>
      </Dropdown>
    </div>
  )
}

const NewMultipleSelectInput = ({ filter, params, onChange }: IDoubleSelect) => {
  const t = useLocale()
  const optionMap = filter.optionMap
  const keys = useMemo(() => Object.keys(optionMap ? optionMap : {}) || [], [optionMap])
  const [currentKey, setCurrentKey] = useState<string>(keys?.[0] || '')
  const [currentValue, setCurrentValue] = useState<string | undefined>(undefined)
  const [minValue, setMinValue] = useState<number | undefined>()
  const [maxValue, setMaxValue] = useState<number | undefined>()
  const [popupVisible, setPopupVisible] = useState(false)

  const currentOptions = useMemo(() => {
    const data = optionMap?.[currentKey]?.data || []
    // 扁平化数组，处理可能存在的嵌套数组情况
    return data.flat()
  }, [currentKey, optionMap])

  // 初始化：只在 params 变化时同步状态
  useEffect(() => {
    // 如果 popup 打开中，不执行任何同步逻辑，避免干扰用户操作
    if (popupVisible) {
      return
    }

    // 根据 params 传进来的值做回显
    const paramKey = keys.find((key) => params[key])

    if (paramKey && params[paramKey]) {
      const paramValue = params[paramKey]

      // 检查当前状态是否已经和 params 同步
      const isAlreadySynced = currentKey === paramKey && currentValue === paramValue

      if (!isAlreadySynced) {
        setCurrentKey(paramKey)
        setCurrentValue(paramValue)

        // 使用当前 key 对应的 options 来判断
        const options = (optionMap?.[paramKey]?.data || []).flat()
        const active = options?.find((d) => d.id === paramValue)

        if (active) {
          setMinValue(undefined)
          setMaxValue(undefined)
        } else {
          //自定义输入的，需要拆分出 min 和 max
          if (paramValue?.includes('-')) {
            const [min, max] = paramValue.split('-')
            setMinValue(Number(min))
            setMaxValue(Number(max))
          } else if (paramValue?.includes('>')) {
            const min = paramValue.replace('>', '')
            setMinValue(Number(min))
            setMaxValue(undefined)
          } else if (paramValue?.includes('<')) {
            const max = paramValue.replace('<', '')
            setMinValue(undefined)
            setMaxValue(Number(max))
          }
        }
      }
    } else {
      // params 中不包含 keys 里的任何值，说明参数被外部重置了，重置所有状态
      const hasAnyValue = currentValue !== undefined || minValue !== undefined || maxValue !== undefined
      if (hasAnyValue) {
        setCurrentKey(keys[0] || '')
        setCurrentValue(undefined)
        setMinValue(undefined)
        setMaxValue(undefined)
      }
    }
  }, [params, optionMap, keys, currentKey, currentValue, minValue, maxValue, popupVisible])

  const triggerText = useMemo(() => {
    // 选项显示
    const active = currentOptions?.find((d) => d.id === currentValue && currentValue !== undefined)

    if (currentValue && active) {
      if (active) {
        return `${optionMap?.[currentKey]?.name || filter.label}: ${active.name}`
      } else {
        //自定义输入的
        if (minValue && maxValue) {
          return `${optionMap?.[currentKey]?.name || filter.label}: ${minValue}-${maxValue}`
        } else if (minValue) {
          return `${optionMap?.[currentKey]?.name || filter.label}: >${minValue}`
        } else if (maxValue) {
          return `${optionMap?.[currentKey]?.name || filter.label}: <${maxValue}`
        }
      }
    } else if (currentValue && !active) {
      if (minValue && maxValue) {
        return `${optionMap?.[currentKey]?.name || filter.label}: ${minValue}-${maxValue}`
      } else if (minValue) {
        return `${optionMap?.[currentKey]?.name || filter.label}: >${minValue}`
      } else if (maxValue) {
        return `${optionMap?.[currentKey]?.name || filter.label}: <${maxValue}`
      }
    } else if (!currentValue && !active) {
      return filter.label
    } else {
      return optionMap?.[currentKey]?.name || filter.label
    }
  }, [currentKey, currentValue, filter.label, currentOptions, optionMap, minValue, maxValue])

  const handleReset = useCallback(() => {
    setCurrentValue(undefined)
    onChange?.({ [currentKey]: undefined })
    setMinValue(undefined)
    setMaxValue(undefined)
    setPopupVisible(false)
  }, [currentKey, onChange])

  const handleOk = useCallback(() => {
    // 先要清空其他选项
    keys
      .filter((key) => key !== currentKey)
      .forEach((key) => {
        onChange?.({ [key]: undefined })
      })

    // 优先使用自定义输入的值（minValue 或 maxValue）
    if (minValue || maxValue) {
      if (minValue && maxValue) {
        setCurrentValue(`${minValue}-${maxValue}`)
        onChange?.({ [currentKey]: `${minValue}-${maxValue}` })
      } else if (minValue) {
        setCurrentValue(`>${minValue}`)
        onChange?.({ [currentKey]: `>${minValue}` })
      } else if (maxValue) {
        setCurrentValue(`<${maxValue}`)
        onChange?.({ [currentKey]: `<${maxValue}` })
      }
    }
    setPopupVisible(false)
  }, [keys, currentKey, currentValue, minValue, maxValue, onChange])

  const handleItemClick = useCallback(
    (id: string | undefined) => {
      // 先要清空其他选项
      keys
        .filter((key) => key !== currentKey)
        .forEach((key) => {
          onChange?.({ [key]: undefined })
        })
      setCurrentValue(id)
      onChange?.({ [currentKey]: id })
      setMinValue(undefined)
      setMaxValue(undefined)
      setPopupVisible(false)
    },
    [keys, currentKey, onChange]
  )

  const handleKeyChange = useCallback(
    (value: any) => {
      // 清空其他 key 的值
      keys.forEach((key) => {
        if (key !== value) {
          onChange?.({ [key]: undefined })
        }
      })

      setCurrentKey(value)
      setMinValue(undefined)
      setMaxValue(undefined)
      setCurrentValue(undefined)
    },
    [keys, onChange]
  )

  const DropList = useMemo(() => {
    return (
      <div className={styles['multiple-select-input-wrap']}>
        <div
          className="flex flex-col gap-2 pr-2"
          style={{
            borderRight: '1px solid #e1e3ee',
            whiteSpace: 'nowrap',
            width: 'auto',
            minWidth: 'fit-content'
          }}
        >
          <RadioGroup direction="vertical" value={currentKey} onChange={handleKeyChange}>
            {keys.map((key) => {
              return (
                <Radio value={key} key={key}>
                  {optionMap?.[key].name}
                </Radio>
              )
            })}
          </RadioGroup>
        </div>
        <div className="flex flex-col gap-2 pl-2">
          {currentOptions?.map((item) => {
            return (
              <div
                key={item.key}
                onClick={() => handleItemClick(item.id)}
                className={cn(
                  'cursor-pointer hover:text-primary hover:bg-gray-100 rounded-md px-1 py-2',
                  currentValue === item.id && 'text-primary bg-gray-100'
                )}
              >
                {item.name}
              </div>
            )
          })}
          <Divider className="my-2" />
          <div className="p-2">
            <div className="flex items-center gap-2 mb-2">
              <InputNumber
                min={0}
                className={styles1.customInput}
                hideControl
                size="small"
                value={minValue}
                onChange={(val) => setMinValue(val)}
              />
              <span className={styles.hyphen}>-</span>
              <InputNumber
                min={0}
                className={styles1.customInput}
                hideControl
                size="small"
                value={maxValue}
                onChange={(val) => setMaxValue(val)}
              />
            </div>
            <div className="flex items-center justify-between">
              <Button type="text" size="small" onClick={handleReset}>
                {t['common.list.reset']}
              </Button>
              <Button className={styles1.okBtn} type="primary" size="small" onClick={handleOk}>
                {t['common.list.ok']}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }, [
    currentKey,
    keys,
    optionMap,
    currentOptions,
    currentValue,
    minValue,
    maxValue,
    handleItemClick,
    handleKeyChange,
    handleReset,
    handleOk,
    t
  ])

  return (
    <div>
      <Dropdown
        droplist={DropList}
        position="bl"
        trigger={'click'}
        popupVisible={popupVisible}
        onVisibleChange={(visible) => {
          setPopupVisible(visible)
        }}
      >
        <div
          className={styles['trigger-button']}
          onClick={(e) => {
            e.stopPropagation()
            setPopupVisible(true)
          }}
        >
          {triggerText}{' '}
          {popupVisible ? <IconUp style={{ paddingLeft: 10 }} /> : <IconDown style={{ paddingLeft: 10 }} />}
        </div>
      </Dropdown>
    </div>
  )
}

interface NewFilterItemProps {
  filter: MoreFilterItem
  params: { [key: string]: any }
  onChange: (params: { [key: string]: any }) => void
  type: FilterItemType
}

export const NewFilterItem = ({ filter, params, onChange, type }: NewFilterItemProps) => {
  const handleChange = (value: string | undefined) => {
    onChange?.({ [filter.key]: value })
  }
  if (type === 'multipleCheckbox') {
    return <NewMultipleCheckbox filter={filter} params={params} onChange={onChange} />
  }
  if (type === 'dateRangePicker') {
    return <NewDateRangePicker params={params} onChange={onChange} tooltipText={filter.tooltipText || ''} />
  }
  if (type === 'multipleSelectInput') {
    return <NewMultipleSelectInput filter={filter} params={params} onChange={onChange} />
  }
  return (
    <FilterItem
      tooltipText={filter.tooltipText || ''}
      title={filter.label}
      data={filter.options?.map((item) => ({ label: item.name, value: item.id })) || []}
      type={type}
      onChange={handleChange}
      defaultValue={params[filter.key]}
    />
  )
}
